import { QueryClient } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { removeOldestQuery } from '@tanstack/react-query-persist-client';
import { ACTIVE_WORKSPACE_COOKIE, getCookie } from './common';

const CACHE_STORAGE_KEY = 'sniperly:query-cache';
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * One cache for every page's API data. A page visited before paints at once
 * from what is cached and is freshened behind it, so moving between pages
 * never shows a skeleton twice for the same data.
 *
 * `staleTime` stops quick back-and-forth navigation from refetching every
 * time; `gcTime` has to be at least the persister's `maxAge`, or entries would
 * be dropped from memory before they could be restored after a reload.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: DAY_MS,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Keeps the cache across reloads, so a refreshed tab opens on the data it last
 * showed instead of a skeleton. If localStorage fills up, the oldest entries go
 * first; if it is unavailable, the cache simply stays in memory.
 */
export const queryPersister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  key: CACHE_STORAGE_KEY,
  throttleTime: 1000,
  retry: removeOldestQuery,
});

export const persistOptions = {
  persister: queryPersister,
  maxAge: DAY_MS,
  // Bump when a cached response shape changes, so old entries are discarded.
  buster: 'v1',
  dehydrateOptions: {
    shouldDehydrateQuery: query => query.state.status === 'success' && query.meta?.persist !== false,
  },
};

/**
 * Every key starts with the active workspace, so switching workspace (or a
 * reload landing on another one) can never show the previous workspace's data.
 */
export const workspaceScope = () => getCookie(ACTIVE_WORKSPACE_COOKIE) || 'default';

/** The key useServiceQuery files a query under; for reading or writing the cache directly. */
export const serviceQueryKey = (name, params = null) => [workspaceScope(), name, params];

/** Drops every cached response, in memory and in localStorage. Used on sign-in and sign-out. */
export function clearQueryCache() {
  queryClient.cancelQueries();
  queryClient.clear();
  try {
    window.localStorage.removeItem(CACHE_STORAGE_KEY);
  } catch {
    // Storage blocked (private mode); nothing was persisted.
  }
}

/** Marks everything cached as stale: mounted pages refetch, others refetch when next opened. */
export function markQueryCacheStale() {
  queryClient.invalidateQueries({ refetchType: 'none' });
}
